import type { SelectChangeEvent } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  Box,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Button as CommonBtn } from '@components/index';
import { PageTemplate } from '@components/template';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { ChevronIcon } from '@icons/ChevronIcon';
import type { TenantSsoProviderPublicDTO } from '@repositories/tenantSsoProviderRepository/tenantSsoProvider.types';
import { getSsoProviderIcon, useSsoRedirect } from '@customHooks/useSsoRedirect';
import { useLookupSsoProviders } from '@queryHooks/useTenantSsoProvider';
import { saveRouteAfterLogin } from '@utils/routeStorage';

type SsoStep = 'EMAIL_INPUT' | 'PROVIDER_SELECT';

export const SsoLoginPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId } = useParams();

  const {
    uiStore: { alertStore },
  } = useStore();

  const isManagerPage = !!tenantId;

  const [step, setStep] = useState<SsoStep>('EMAIL_INPUT');
  const [email, setEmail] = useState('');
  const [providers, setProviders] = useState<TenantSsoProviderPublicDTO[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);

  const { redirectToSsoProvider, isRedirecting } = useSsoRedirect();

  const { mutate: lookupSsoProviders, isPending: isLookupPending } = useLookupSsoProviders();

  // routeAfterLogin 보존: 마운트 시 location.state에서 가져와 localStorage에 저장
  // OIDC 리다이렉트 후에도 원래 경로로 복귀 보장
  useEffect(() => {
    const routeAfterLogin = location.state?.routeAfterLogin;
    if (routeAfterLogin) {
      saveRouteAfterLogin(routeAfterLogin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateToLogin = () => {
    if (isManagerPage) {
      navigate(`/manager/${tenantId}/login`);
    } else {
      navigate('/login');
    }
  };

  const handleGoBack = () => {
    if (step === 'PROVIDER_SELECT') {
      setStep('EMAIL_INPUT');
      setProviders([]);
      setSelectedProviderId(null);
      return;
    }

    navigateToLogin();
  };

  const showError = (message: string) => {
    alertStore.open({
      title: t('Sign_In_01'),
      message,
      useCancel: false,
    });
  };

  const handleEmailSubmit = () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    lookupSsoProviders(
      { username: trimmedEmail },
      {
        onSuccess: data => {
          if (data.length === 0) {
            showError(t('SsoProvider.Login.NoProvider'));
            return;
          }

          // 프로바이더가 1개면 선택 단계 없이 바로 SSO 리다이렉트
          if (data.length === 1) {
            redirectToSsoProvider(data[0], trimmedEmail);
            return;
          }

          setProviders(data);
          setStep('PROVIDER_SELECT');
        },
        onError: () => {
          showError(t('SsoProvider.Login.LookupError'));
        },
      },
    );
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEmailSubmit();
    }
  };

  const handleProviderLogin = () => {
    if (!selectedProviderId) return;
    const selected = providers.find(p => `${p.tenantId}:${p.registrationId}` === selectedProviderId);
    if (selected) {
      redirectToSsoProvider(selected, email.trim());
    }
  };

  return (
    <PageTemplate>
      <Container>
        <ContentWrapper>
          {/* 뒤로가기 */}
          <GoBackButton onClick={handleGoBack}>
            <ChevronIcon width={16} height={16} rotateNum={-90} color={theme.colors['ic-gray-main']} />
            <GoBackText>{t('SsoProvider.Login.BackToLogin')}</GoBackText>
          </GoBackButton>

          {/* 타이틀 */}
          <TitleText variant="headline3">{t('SsoProvider.Login.Button')}</TitleText>

          {/* Step 1: 이메일 입력 */}
          {step === 'EMAIL_INPUT' && (
            <StepContainer>
              <SubtitleText variant="body2" color={theme.colors['text-gray-sub-dark']}>
                {t('SsoProvider.Login.EmailGuide')}
              </SubtitleText>
              <EmailRow>
                <EmailInput
                  placeholder={t('SsoProvider.Login.EmailPlaceholder')}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={handleEmailKeyDown}
                  autoFocus
                  disabled={isLookupPending}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon>@</EmailIcon>
                      </InputAdornment>
                    ),
                  }}
                />
                <ContinueButton
                  size="extraLarge"
                  variant="contain"
                  onClick={handleEmailSubmit}
                  disabled={!email.trim() || isLookupPending}
                >
                  {isLookupPending ? <CircularProgress size={16} color="inherit" /> : t('SsoProvider.Login.Continue')}
                </ContinueButton>
              </EmailRow>
            </StepContainer>
          )}

          {/* Step 2: 프로바이더 선택 */}
          {step === 'PROVIDER_SELECT' && (
            <StepContainer>
              <EmailBadge>
                <EmailBadgeText>{email}</EmailBadgeText>
              </EmailBadge>
              <SubtitleText variant="body2" color={theme.colors['text-gray-sub-dark']}>
                {t('SsoProvider.Login.SelectGuide')}
              </SubtitleText>
              <ProviderSelect
                displayEmpty
                value={selectedProviderId ?? ''}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  if (typeof e.target.value === 'string') {
                    setSelectedProviderId(e.target.value);
                  }
                }}
                disabled={isRedirecting}
                renderValue={(value: unknown) => {
                  if (typeof value !== 'string' || !value) {
                    return <SelectPlaceholder>{t('SsoProvider.Login.SelectPlaceholder')}</SelectPlaceholder>;
                  }
                  const selected = providers.find(p => `${p.tenantId}:${p.registrationId}` === value);
                  if (!selected) return null;
                  return (
                    <SelectedProviderRow>
                      {getSsoProviderIcon(selected.issuerUri, 18)}
                      <SelectedProviderName>{selected.providerName}</SelectedProviderName>
                    </SelectedProviderRow>
                  );
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      marginTop: '4px',
                      borderRadius: '5px',
                      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
                    },
                  },
                }}
              >
                {providers.map(provider => {
                  const providerId = `${provider.tenantId}:${provider.registrationId}`;
                  return (
                    <ProviderMenuItem key={providerId} value={providerId}>
                      <ProviderMenuItemContent>
                        {getSsoProviderIcon(provider.issuerUri, 18)}
                        <ProviderMenuItemText>
                          <span>{provider.providerName}</span>
                          {provider.tenantName && <ProviderMenuItemSub>{provider.tenantName}</ProviderMenuItemSub>}
                        </ProviderMenuItemText>
                      </ProviderMenuItemContent>
                    </ProviderMenuItem>
                  );
                })}
              </ProviderSelect>
              <LoginButton
                size="extraLarge"
                variant="contain"
                onClick={handleProviderLogin}
                disabled={!selectedProviderId || isRedirecting}
              >
                {isRedirecting ? <CircularProgress size={16} color="inherit" /> : t('SsoProvider.Login.LoginButton')}
              </LoginButton>
            </StepContainer>
          )}
        </ContentWrapper>
      </Container>
    </PageTemplate>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 120px;
  min-height: calc(100vh - 70px - var(--employee-gnb-height));
  height: 100%;
  background-color: ${({ theme }) => theme.colors['bg-white']};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 460px;
`;

const GoBackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: 28px;

  &:hover span {
    text-decoration: underline;
  }
`;

const GoBackText = styled.span`
  ${fonts.Body2};
  color: ${({ theme }) => theme.colors['text-gray-main']};
`;

const TitleText = styled(Typography)`
  margin-bottom: 8px;
` as typeof Typography;

const SubtitleText = styled(Typography)`
  margin-bottom: 20px;
`;

const StepContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const EmailRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const EmailIcon = styled.span`
  ${fonts.Body2};
  color: ${({ theme }) => theme.colors['text-gray-light']};
  font-weight: 500;
`;

const EmailInput = styled(TextField)`
  flex: 1;

  & .MuiInputBase-root {
    height: 44px;
    border-radius: 5px;
  }
  & .MuiInputBase-input {
    ${fonts.Body2};
  }
  & .MuiInputBase-input::placeholder {
    color: ${({ theme }) => theme.colors['text-gray-light']};
  }
`;

const ContinueButton = styled(CommonBtn)`
  height: 44px;
  padding: 0 24px;
  border-radius: 5px;
  background: ${({ theme }) => theme.colors['bg-gradient']};
  color: ${({ theme }) => theme.colors['text-white']};
  ${fonts.Button2};
  flex-shrink: 0;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors['state-gradient-hover']};
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const EmailBadge = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 4px 12px;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.colors['bg-gray-lighter']};
  border: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  margin-bottom: 8px;
`;

const EmailBadgeText = styled.span`
  ${fonts.Caption2};
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
`;

const ProviderSelect = styled(Select)`
  width: 100%;
  height: 44px;
  border-radius: 5px;
  margin-bottom: 20px;

  & .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors['border-gray-light']};
    border-radius: 5px;
  }

  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors['border-gray-dark']};
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors['border-primary']};
  }

  & .MuiSelect-select {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    height: 100% !important;
    box-sizing: border-box;
    ${fonts.Body2};
    color: ${({ theme }) => theme.colors['text-gray-main']};
  }
`;

const SelectPlaceholder = styled.span`
  ${fonts.Body2};
  color: ${({ theme }) => theme.colors['text-gray-light']};
`;

const SelectedProviderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SelectedProviderName = styled.span`
  ${fonts.Body2};
  color: ${({ theme }) => theme.colors['text-gray-main']};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProviderMenuItem = styled(MenuItem)`
  padding: 10px 12px;

  &:hover {
    background-color: ${({ theme }) => theme.colors['state-white-primary-hover']};
  }

  &.Mui-selected {
    background-color: ${({ theme }) => theme.colors['state-white-primary-pressed']};

    &:hover {
      background-color: ${({ theme }) => theme.colors['state-white-primary-hover']};
    }
  }
`;

const ProviderMenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const ProviderMenuItemText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  & > span {
    ${fonts.Body2};
    color: ${({ theme }) => theme.colors['text-gray-main']};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ProviderMenuItemSub = styled.span`
  ${fonts.Caption2};
  color: ${({ theme }) => theme.colors['text-gray-light']};
`;

const LoginButton = styled(CommonBtn)`
  width: 100%;
  height: 32px;
  border-radius: 5px;
  background: ${({ theme }) => theme.colors['bg-gradient']};
  color: ${({ theme }) => theme.colors['text-white']};
  ${fonts.Button2};

  &:hover {
    background: ${({ theme }) => theme.colors['state-gradient-hover']};
  }

  &:disabled {
    opacity: 0.5;
  }
`;
